import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ScenarioConfig, ScenarioIssue, ScenarioPreview } from '../components/scenario/types';
import { emptyConfig, formIncompatibility } from '../components/scenario/scenarioDraft';
import { useScenario } from './useScenario';

export type ScenarioEditorMode = 'form' | 'json' | 'flow';
export type ScenarioStatus = { type: 'success' | 'error' | 'info'; text: string };

const VALIDATE_DEBOUNCE_MS = 800;

const toText = (config: unknown) => JSON.stringify(config, null, 2);

const parseText = (text: string): { value: unknown; error: string | null } => {
  try {
    return { value: JSON.parse(text), error: null };
  } catch (err) {
    return { value: null, error: err instanceof Error ? err.message : String(err) };
  }
};

export const useScenarioDraft = (agentId?: string) => {
  const { t } = useTranslation();
  const { scenario, meta, loading, loadError, save, validate, testTool } = useScenario(agentId);

  const [mode, setMode] = useState<ScenarioEditorMode>('form');
  const [draft, setDraft] = useState<ScenarioConfig | null>(null);
  const [text, setText] = useState('');
  const [enabled, setEnabledState] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [issues, setIssues] = useState<ScenarioIssue[]>([]);
  const [warnings, setWarnings] = useState<ScenarioIssue[]>([]);
  const [preview, setPreview] = useState<ScenarioPreview | null>(null);
  const [status, setStatus] = useState<ScenarioStatus | null>(null);
  const [modeError, setModeError] = useState<string | null>(null);
  const initializedFor = useRef<string | null>(null);

  const reset = useCallback((config: ScenarioConfig, isEnabled: boolean) => {
    setDraft(config);
    setText(toText(config));
    setEnabledState(isEnabled);
    setDirty(false);
    setIssues([]);
    setWarnings([]);
    setPreview(null);
  }, []);

  useEffect(() => {
    if (!agentId || !scenario || initializedFor.current === agentId) return;
    initializedFor.current = agentId;
    reset(scenario.active_version?.config ?? emptyConfig(), scenario.enabled);
  }, [agentId, scenario, reset]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const parsed = useMemo(() => parseText(text), [text]);
  // The form and the read-only flow both work on `draft`; only the JSON mode edits the text.
  const currentConfig: unknown = mode !== 'json' ? draft : parsed.error ? null : parsed.value;

  const runValidation = useCallback(
    async (config: unknown) => {
      const outcome = await validate(config);
      if (outcome.ok) {
        setIssues(outcome.data.errors);
        setWarnings(outcome.data.warnings);
        setPreview(outcome.data.preview);
        return outcome.data.valid;
      }
      setStatus({ type: 'error', text: outcome.message });
      return false;
    },
    [validate]
  );

  useEffect(() => {
    if (!dirty || currentConfig === null) return undefined;
    const timer = window.setTimeout(() => runValidation(currentConfig), VALIDATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [currentConfig, dirty, runValidation]);

  const markDirty = () => {
    setDirty(true);
    setStatus(null);
  };

  const changeDraft = (next: ScenarioConfig) => {
    setDraft(next);
    markDirty();
  };

  const changeText = (next: string) => {
    setText(next);
    markDirty();
  };

  const setEnabled = (value: boolean) => {
    setEnabledState(value);
    markDirty();
  };

  const switchMode = (next: ScenarioEditorMode) => {
    setModeError(null);
    if (next === mode) return;
    if (next === 'json') {
      setText(toText(draft));
      setMode('json');
      return;
    }
    if (mode !== 'json') {
      setMode(next);
      return;
    }
    if (parsed.error) {
      setModeError(`${t('agents.scenario.jsonParseError')}: ${parsed.error}`);
      return;
    }
    const incompatible = formIncompatibility(parsed.value);
    if (incompatible) {
      setModeError(t('agents.scenario.formIncompatible', { path: incompatible }));
      return;
    }
    setDraft(parsed.value as ScenarioConfig);
    setMode(next);
  };

  const loadExample = (key: string) => {
    const example = meta?.examples[key];
    if (!example) return;
    if (dirty && !window.confirm(t('agents.scenario.confirmReplaceDraft'))) return;
    setDraft(example);
    setText(toText(example));
    markDirty();
  };

  const discard = () => {
    if (!scenario) return;
    if (!window.confirm(t('agents.scenario.confirmDiscard'))) return;
    reset(scenario.active_version?.config ?? emptyConfig(), scenario.enabled);
    setStatus(null);
    setModeError(null);
    setMode('form');
  };

  const validateNow = () => {
    if (currentConfig !== null) runValidation(currentConfig);
  };

  // Returns false when the agent must not be saved because the scenario draft is invalid.
  const prepareSave = async (): Promise<boolean> => {
    if (!dirty) return true;
    const valid = currentConfig !== null && (await runValidation(currentConfig));
    if (!valid) setStatus({ type: 'error', text: t('agents.scenario.saveBlocked') });
    return valid;
  };

  const commit = async (): Promise<boolean> => {
    if (!dirty) return true;
    if (currentConfig === null) return false;
    const outcome = await save(currentConfig as ScenarioConfig, enabled);
    if (outcome.ok) {
      reset(outcome.data.active_version?.config ?? emptyConfig(), outcome.data.enabled);
      setStatus({
        type: 'success',
        text: t('agents.scenario.savedVersion', { number: outcome.data.active_version?.number ?? '-' }),
      });
      return true;
    }
    setIssues(outcome.errors);
    setWarnings(outcome.warnings);
    setStatus({
      type: 'error',
      text: outcome.errors.length
        ? t('agents.scenario.saveRejected', { count: outcome.errors.length })
        : outcome.message,
    });
    return false;
  };

  return {
    scenario,
    meta,
    loading,
    loadError,
    testTool,
    mode,
    draft,
    text,
    parseError: parsed.error,
    currentConfig,
    enabled,
    dirty,
    issues,
    warnings,
    preview,
    status,
    modeError,
    changeDraft,
    changeText,
    setEnabled,
    switchMode,
    loadExample,
    discard,
    validateNow,
    prepareSave,
    commit,
  };
};

export type ScenarioDraftController = ReturnType<typeof useScenarioDraft>;
